import { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, CalendarCheck2, ClipboardList, CreditCard, FileText, FlaskConical, Pill } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { EmptyState, ErrorBanner, MetricCard, Panel, PortalPage, TableCard, formatPortalCurrency, formatPortalDateTime, statusPillClass } from './ui';

type PortalOverviewResponse = {
  data?: {
    upcomingAppointments: number;
    pendingForms: number;
    openInvoices: number;
    activePrescriptions: number;
    labReports: number;
  };
};

type HomeOverview = NonNullable<PortalOverviewResponse['data']> & {
  outstandingBalance: number;
};

type PortalAppointmentResponse = {
  data?: Array<{
    id: string;
    status: string;
    title?: string | null;
    startTime: string;
    endTime: string;
    appointmentType?: { name: string; isTelemed?: boolean } | null;
    provider?: { name: string } | null;
  }>;
};

type PortalTimelineResponse = {
  data?: Array<{
    id: string;
    type: string;
    title: string;
    at: string;
    detail: string;
    status: string;
  }>;
};

type PortalFormItem = {
  id: string;
  status: string;
  completionPercent: number;
  dueDate?: string | null;
  template: { name: string; estimatedMinutes?: number | null };
};

type PortalDocItem = {
  id: string;
  fileName: string;
  title?: string | null;
  reviewStatus?: string | null;
  rejectionReason?: string | null;
};

export default function HomePage() {
  const { t } = useTranslation();
  const { fetchJson } = useApiClient();
  const { buildPortalPath, profile, refreshNonce } = usePortalPatientData();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<HomeOverview | null>(null);
  const [appointments, setAppointments] = useState<PortalAppointmentResponse['data']>([]);
  const [activity, setActivity] = useState<PortalTimelineResponse['data']>([]);
  const [pendingForms, setPendingForms] = useState<PortalFormItem[]>([]);
  const [rejectedDocs, setRejectedDocs] = useState<PortalDocItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHome = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Load each section independently so one failing/missing endpoint can't
      // blank the whole dashboard — render whatever succeeds.
      const [overviewResult, appointmentResult, timelineResult, billingResult, formsResult, docsResult] = await Promise.allSettled([
        fetchJson<PortalOverviewResponse>(buildPortalPath('/portal/overview')),
        fetchJson<PortalAppointmentResponse>(buildPortalPath('/portal/appointments?limit=5')),
        fetchJson<PortalTimelineResponse>(buildPortalPath('/portal/timeline?limit=4')),
        fetchJson<{ data?: Array<{ balance: number }> }>(buildPortalPath('/portal/billing?limit=50')),
        fetchJson<{ data?: PortalFormItem[] }>(buildPortalPath('/portal/forms?limit=10')),
        fetchJson<{ data?: PortalDocItem[] }>(buildPortalPath('/portal/documents?limit=20')),
      ]);

      const overviewResponse = overviewResult.status === 'fulfilled' ? overviewResult.value : null;
      const appointmentResponse = appointmentResult.status === 'fulfilled' ? appointmentResult.value : null;
      const timelineResponse = timelineResult.status === 'fulfilled' ? timelineResult.value : null;
      const billingResponse = billingResult.status === 'fulfilled' ? billingResult.value : null;
      const formsResponse = formsResult.status === 'fulfilled' ? formsResult.value : null;
      const docsResponse = docsResult.status === 'fulfilled' ? docsResult.value : null;

      const balances = Array.isArray(billingResponse?.data) ? billingResponse.data : [];
      const outstandingBalance = balances.reduce((sum, row) => sum + Number(row.balance || 0), 0);
      setOverview(overviewResponse?.data ? { ...overviewResponse.data, outstandingBalance } : null);
      setAppointments(Array.isArray(appointmentResponse?.data) ? appointmentResponse.data : []);
      setActivity(Array.isArray(timelineResponse?.data) ? timelineResponse.data : []);

      const allForms = Array.isArray(formsResponse?.data) ? formsResponse.data : [];
      setPendingForms(allForms.filter((f) => f.status !== 'COMPLETED' && f.status !== 'CANCELLED' && f.status !== 'EXPIRED'));

      const allDocs = Array.isArray(docsResponse?.data) ? docsResponse.data : [];
      setRejectedDocs(allDocs.filter((d) => d.reviewStatus === 'REJECTED'));

      // Surface an error banner only if every section failed.
      const allFailed = [overviewResult, appointmentResult, timelineResult, billingResult, formsResult, docsResult].every(
        (result) => result.status === 'rejected',
      );
      if (allFailed) {
        const firstReason = [overviewResult, appointmentResult, timelineResult, billingResult, formsResult, docsResult].find(
          (result): result is PromiseRejectedResult => result.status === 'rejected',
        )?.reason;
        setError(firstReason?.message || t('home.loadError'));
      }
    } catch (err: any) {
      setError(err?.message || t('home.loadError'));
    } finally {
      setLoading(false);
    }
  }, [buildPortalPath, fetchJson, t]);

  useEffect(() => {
    void loadHome();
  }, [loadHome, refreshNonce]);

  return (
    <PortalPage
      title={`${profile?.firstName || t('common.patient')} ${profile?.lastName || ''}`.trim() || t('home.workspaceFallback')}
      description={t('home.description')}
    >
      {error ? <ErrorBanner message={error} /> : null}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <MetricCard title={t('home.metrics.upcomingVisits')} value={overview?.upcomingAppointments ?? 0} icon={CalendarCheck2} color="text-blue-600" />
        <MetricCard title={t('home.metrics.formsPending')} value={overview?.pendingForms ?? 0} icon={ClipboardList} color="text-amber-600" />
        <MetricCard title={t('home.metrics.openInvoices')} value={overview?.openInvoices ?? 0} icon={CreditCard} color="text-rose-600" />
        <MetricCard title={t('home.metrics.activeMeds')} value={overview?.activePrescriptions ?? 0} icon={Pill} color="text-emerald-600" />
        <MetricCard title={t('home.metrics.labReports')} value={overview?.labReports ?? 0} icon={FlaskConical} color="text-sky-600" />
        <MetricCard title={t('home.metrics.outstanding')} value={formatPortalCurrency(overview?.outstandingBalance ?? 0)} icon={Activity} color="text-purple-600" />
      </div>

      {!loading && (pendingForms.length > 0 || rejectedDocs.length > 0) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pendingForms.length > 0 ? (
            <button
              type="button"
              onClick={() => navigate('/forms')}
              className="bg-white p-5 rounded-xl shadow-sm border border-slate-100 text-left hover:border-lime-300 transition-colors"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">
                    {t('home.formsToComplete', { count: pendingForms.length })}
                  </p>
                  <p className="text-xs text-slate-500">{t('home.formsToCompleteBody')}</p>
                </div>
              </div>
              <div className="space-y-1">
                {pendingForms.slice(0, 3).map((f) => (
                  <div key={f.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700 truncate">{f.template.name}</span>
                    <span className="text-xs font-medium text-lime-700">{f.completionPercent}%</span>
                  </div>
                ))}
                {pendingForms.length > 3 ? (
                  <p className="text-xs text-slate-400">{t('home.moreItems', { count: pendingForms.length - 3 })}</p>
                ) : null}
              </div>
            </button>
          ) : null}
          {rejectedDocs.length > 0 ? (
            <button
              type="button"
              onClick={() => navigate('/medical-records')}
              className="bg-white p-5 rounded-xl shadow-sm border border-red-100 text-left hover:border-red-300 transition-colors"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800">
                    {t('home.docsNeedAttention', { count: rejectedDocs.length })}
                  </p>
                  <p className="text-xs text-slate-500">{t('home.docsNeedAttentionBody')}</p>
                </div>
              </div>
              <div className="space-y-1">
                {rejectedDocs.slice(0, 3).map((d) => (
                  <div key={d.id} className="text-sm">
                    <span className="text-slate-700 truncate">{d.title || d.fileName}</span>
                    {d.rejectionReason ? (
                      <p className="text-xs text-red-500 truncate">{d.rejectionReason}</p>
                    ) : null}
                  </div>
                ))}
                {rejectedDocs.length > 3 ? (
                  <p className="text-xs text-slate-400">{t('home.moreItems', { count: rejectedDocs.length - 3 })}</p>
                ) : null}
              </div>
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title={t('home.recentActivity')}>
          {loading ? (
            <div className="text-sm text-slate-500">{t('home.loadingActivity')}</div>
          ) : activity && activity.length ? (
            <div className="space-y-2">
              {activity.map((item) => (
                <div key={item.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <p className="font-medium text-slate-800">{item.title}</p>
                  <p className="text-xs text-slate-500">
                    {item.type} · {formatPortalDateTime(item.at)}
                  </p>
                  <p className="mt-1 text-sm text-slate-600">{item.detail}</p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState message={t('home.noRecentActivity')} />
          )}
        </Panel>

        <Panel title={t('home.upcomingSnapshot')}>
          <TableCard
            headers={[t('home.tableType'), t('home.tableDateTime'), t('home.tableProvider'), t('home.tableStatus')]}
            rows={appointments.map((item) => [
              <span key={`${item.id}-type`} className="font-medium text-slate-700">
                {item.title || item.appointmentType?.name || t('common.appointment')}
              </span>,
              formatPortalDateTime(item.startTime),
              item.provider?.name || t('common.careTeam'),
              <span key={`${item.id}-status`} className={`pill ${statusPillClass(item.status)}`}>
                {item.status}
              </span>,
            ])}
            emptyMessage={t('home.noUpcoming')}
          />
        </Panel>
      </div>
    </PortalPage>
  );
}
